import { CoreCurrencyIcon } from '@components'
import CoreLoader from '@components/CoreLoader'
import { breakpoints } from '@constants'
import { formatCurrencyForDisplay } from '@utils'
import { useCurrencies, useMyAccountAccessQry } from 'mmfintech-backend-api'
import type { AccountBalanceResponse } from 'mmfintech-backend-api/types'
import { formatFloat, isValidArray, tr, useMatchMedia } from 'mmfintech-commons'
import { DataTable, ErrorDisplay } from 'mmfintech-portal-commons'

import './AccountsOverview.scss'

type AccountOverviewRowProps = {
  id: number
  currencyCode: string
  balance: number
  availableBalance: number
  name: string
  permission?: string
}

type AccountOverviewProps = {
  activeAccounts?: AccountBalanceResponse[]
  accountsError?: unknown
  accountsFetching?: boolean
}

export const AccountsOverview = ({ activeAccounts, accountsFetching, accountsError }: AccountOverviewProps) => {
  const { accountAccess, accountAccessFetching } = useMyAccountAccessQry()
  const { getCurrencyPrecision } = useCurrencies()

  const isLarge = useMatchMedia({
    breakpoint: breakpoints.lg,
    prefix: 'min'
  })

  const accounts = activeAccounts?.map((a: any) => {
    const permission = accountAccess?.find((perm: any) => a?.id === perm?.accountId)
    return { ...a, permission: permission?.accessLevel }
  })

  const getPermissionLabel = (permission?: string) => {
    switch (permission) {
      case 'WRITER':
        return tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.DASHBOARD.ACCOUNT_OVERVIEW.SEND.TITLE', 'Send')
      case 'REQUESTER':
        return tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.DASHBOARD.ACCOUNT_OVERVIEW.INITIATE.TITLE', 'Initiate')
      case 'READER':
        return tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.DASHBOARD.ACCOUNT_OVERVIEW.VIEW.TITLE', 'View')
      default:
        return ''
    }
  }

  return (
    <div className='account-overview-list-wrapper'>
      <h2 className='account-overview-list-title'>{tr('FRONTEND.ACCOUNTS.TITLE', 'Accounts')}</h2>

      <ErrorDisplay error={accountsError} />

      {accountsFetching || accountAccessFetching ? (
        <CoreLoader />
      ) : (
        <div className='table-container'>
          <DataTable.Table>
            <DataTable.Head>
              <DataTable.Row className='headRow'>
                <DataTable.HeadCell>
                  {tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.DASHBOARD.ACCOUNT_OVERVIEW.CURRENCY', 'Currency')}
                </DataTable.HeadCell>
                <DataTable.HeadCell>
                  {tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.DASHBOARD.ACCOUNT_OVERVIEW.ACCOUNT_NAME', 'Account Name')}
                </DataTable.HeadCell>
                <DataTable.HeadCell>
                  {tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.DASHBOARD.ACCOUNT_OVERVIEW.PERMISSIONS', 'Permissions')}
                </DataTable.HeadCell>
                {isLarge && (
                  <DataTable.HeadCell>
                    {tr('FRONTEND.USERS.EXPENSE_MANAGEMENT.DASHBOARD.ACCOUNT_OVERVIEW.BALANCE', 'Balance')}
                  </DataTable.HeadCell>
                )}
              </DataTable.Row>
            </DataTable.Head>
            {isValidArray(accounts) && (
              <DataTable.Body>
                {accounts.map((account: AccountOverviewRowProps) => {
                  const { currencyCode, balance, availableBalance, name, permission, id } = account

                  return (
                    <DataTable.Row key={id}>
                      <DataTable.Cell className='accounts-cell'>
                        <div className='currency-box-container'>
                          <CoreCurrencyIcon currency={currencyCode} size='40px' circle />
                          <div className='currency-box'>
                            <span className='currency'>{formatCurrencyForDisplay(currencyCode)}</span>

                            {!isLarge && (
                              <span>
                                {formatFloat(
                                  availableBalance || balance || 0,
                                  getCurrencyPrecision(currencyCode || '')
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </DataTable.Cell>
                      <DataTable.Cell> {name}</DataTable.Cell>
                      <DataTable.Cell>{getPermissionLabel(permission)}</DataTable.Cell>
                      {isLarge && (
                        <DataTable.Cell>
                          {formatFloat(availableBalance || balance || 0, getCurrencyPrecision(currencyCode || ''))}{' '}
                          {formatCurrencyForDisplay(currencyCode)}
                        </DataTable.Cell>
                      )}
                    </DataTable.Row>
                  )
                })}
              </DataTable.Body>
            )}
          </DataTable.Table>
        </div>
      )}
    </div>
  )
}
